import { createContext, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import usePrevious from '@/hooks/usePrevious'
import actions from '@/actions'

const Context = createContext({})
const { Provider, Consumer } = Context

const ContextComponent = ({
  children,
}) => {
  const [layout, setLayout] = useState('dark')
  const [isNavOpen, setNavOpen] = useState(false)
  const [isFullscreenMode, setFullscreenMode] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [headerRef, setHeaderRef] = useState({})
  const [cookieRef, setCookieRef] = useState({})
  const [headerInverse, setHeaderInverse] = useState(false)
  const [pageAnimationReady, setPageAnimationReady] = useState(false)

  const { content, pathname } = useSelector((state) => ({
    pathname: state.router.location.pathname,
    content: state.page,
  }), shallowEqual)
  const prevPathname = usePrevious(pathname)

  const dispatch = useDispatch()
  const setPageShow = useCallback((value) => dispatch(actions.page.setShow(value)), [dispatch])
  const setPageReady = useCallback((value) => dispatch(actions.loading.setReady(value)), [dispatch])
  const setLoadingFirst = useCallback((value) => dispatch(actions.loading.setFirstLoad(value)), [dispatch])

  useEffect(() => {
    if (prevPathname !== undefined && prevPathname !== pathname) {
      setPageReady(false)
      setLoadingFirst(false)
      setPageShow(false)
      setPageAnimationReady(false)
    }
  }, [pathname])

  const getImageUrl = (index, size = 'full', slug = null, type = 'pages') => {
    let page = null
    if (slug === null) {
      const { current, currentType } = content
      page = currentType === 'pages' ? content.pages : content.posts
      slug = current
    } else {
      page = type === 'pages' ? content.pages : content.posts
    }
    if (`${slug}` in page) {
      page = page[slug].media_post
      if ((page[index] !== undefined) && (page[index].sizes !== undefined)) {
        if (page[index].sizes[size] !== undefined) {
          return `${page[index].source_url}/${page[index].sizes[size].file}`
        }
        return `${page[index].source_url}/${page[index].sizes.full.file}`
      }
    }
    return ''
  }

  const getImageTitle = (index, slug = null, type = 'pages') => {
    let page = null
    if (slug === null) {
      const { current, currentType } = content
      page = currentType === 'pages' ? content.pages : content.posts
      slug = current
    } else {
      page = type === 'pages' ? content.pages : content.posts
    }
    if (`${slug}` in page) {
      page = page[slug].media_post
      if ((page[index] !== undefined) && (page[index].title !== undefined)) {
        return page[index].title
      }
    }
    return ''
  }

  const getImageCaption = (index, slug = null, type = 'pages') => {
    let page = null
    if (slug === null) {
      const { current, currentType } = content
      page = currentType === 'pages' ? content.pages : content.posts
      slug = current
    } else {
      page = type === 'pages' ? content.pages : content.posts
    }
    if (`${slug}` in page) {
      page = page[slug].media_post
      if ((page[index] !== undefined) && (page[index].caption !== undefined)) {
        return page[index].caption
      }
    }
    return ''
  }

  return (
    <Provider value={{
      getImageUrl,
      getImageTitle,
      getImageCaption,
      layout,
      setLayout,
      isNavOpen,
      setNavOpen,
      isFullscreenMode,
      setFullscreenMode,
      headerHeight,
      setHeaderHeight,
      setHeaderRef,
      headerRef,
      setCookieRef,
      cookieRef,
      setHeaderInverse,
      headerInverse,
      setPageAnimationReady,
      pageAnimationReady,
    }}
    >
      { children }
    </Provider>
  )
}

export {
  Context,
  Consumer,
}

export default ContextComponent
